export type AmlTargetType = 'address' | 'transaction';

export interface AmlTargetDetection {
  type: AmlTargetType;
  /** Null means the format is shared by several networks; the provider resolves it. */
  network: string | null;
  candidates: string[];
}

const EVM_NETWORKS = ['ETH', 'BSC', 'POLYGON', 'ARBITRUM', 'OPTIMISM', 'BASE', 'AVAX'];

/**
 * Format detection only — the same rules Reenergy uses. An ambiguous hash stays
 * unresolved until the provider returns a network.
 */
export function detectAmlTarget(raw: string): AmlTargetDetection | null {
  const input = raw.trim();

  if (/^0x[0-9a-fA-F]{64}$/.test(input)) {
    return { type: 'transaction', network: null, candidates: EVM_NETWORKS };
  }
  if (/^[1-9A-HJ-NP-Za-km-z]{87,88}$/.test(input)) {
    return { type: 'transaction', network: 'SOL', candidates: ['SOL'] };
  }
  if (/^[0-9a-fA-F]{64}$/.test(input)) {
    return {
      type: 'transaction',
      network: null,
      candidates: ['TRX', 'BTC', 'LTC', 'DOGE', 'XRP', 'XLM', 'TON'],
    };
  }

  if (/^[13][a-km-zA-HJ-NP-Z1-9]{25,34}$/.test(input)) {
    return { type: 'address', network: 'BTC', candidates: ['BTC'] };
  }
  if (/^bc1[ac-hj-np-z02-9]{6,87}$/i.test(input)) {
    return { type: 'address', network: 'BTC', candidates: ['BTC'] };
  }
  if (/^0x[0-9a-fA-F]{40}$/.test(input)) {
    return { type: 'address', network: 'ETH', candidates: EVM_NETWORKS };
  }
  if (/^T[1-9A-HJ-NP-Za-km-z]{33}$/.test(input)) {
    return { type: 'address', network: 'TRX', candidates: ['TRX'] };
  }
  if (/^[LM][a-km-zA-HJ-NP-Z1-9]{25,34}$/.test(input) || /^ltc1[a-z0-9]{6,87}$/i.test(input)) {
    return { type: 'address', network: 'LTC', candidates: ['LTC'] };
  }
  if (/^r[0-9a-zA-Z]{24,34}$/.test(input)) {
    return { type: 'address', network: 'XRP', candidates: ['XRP'] };
  }
  if (/^D[0-9a-zA-Z]{33}$/.test(input)) {
    return { type: 'address', network: 'DOGE', candidates: ['DOGE'] };
  }
  if (/^G[0-9A-Z]{55}$/.test(input)) {
    return { type: 'address', network: 'XLM', candidates: ['XLM'] };
  }
  if (/^(EQ|UQ)[A-Za-z0-9_-]{46}$/.test(input)) {
    return { type: 'address', network: 'TON', candidates: ['TON'] };
  }
  if (/^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(input)) {
    return { type: 'address', network: 'SOL', candidates: ['SOL'] };
  }

  return null;
}

export function normalizeAmlNetwork(value: unknown): string | null {
  if (typeof value !== 'string' || !value.trim()) return null;
  const normalized = value.trim().toUpperCase();
  if (normalized === 'TRON') return 'TRX';
  return normalized.slice(0, 32);
}

function nestedRecord(value: unknown): Record<string, unknown> | null {
  return value != null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

export function providerAmlIdentity(
  exposure: unknown,
  providerNetwork?: unknown,
): { type?: AmlTargetType; network?: string } {
  const record = nestedRecord(exposure);
  const checkType = typeof record?.checkType === 'string' ? record.checkType.toLowerCase() : '';
  const type =
    record?.transfer != null || ['deposit', 'withdrawal', 'transfer'].includes(checkType)
      ? 'transaction'
      : record?.address != null || checkType === 'single_address'
        ? 'address'
        : undefined;
  const network =
    normalizeAmlNetwork(record?.network) ??
    normalizeAmlNetwork(nestedRecord(record?.transfer)?.network) ??
    normalizeAmlNetwork(nestedRecord(record?.address)?.network) ??
    normalizeAmlNetwork(providerNetwork) ??
    undefined;
  return {
    ...(type ? { type } : {}),
    ...(network ? { network } : {}),
  };
}

/** Reenergy publishes risk as 0..100. We store a 0..1 ratio for the UI. */
export function normalizeRiskLevel(value: number | string | null | undefined): number | null {
  if (value == null || value === '') return null;
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;
  return Math.min(1, Math.max(0, numeric / 100));
}

export function normalizeExposureRiskScore(value: number | string | null | undefined): number | null {
  if (value == null || value === '') return null;
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;
  const ratio = Math.abs(numeric) <= 1 ? numeric : numeric / 100;
  return Math.min(1, Math.max(0, ratio));
}

export function extractProviderRiskLevel(provider: {
  risk_level?: number | string | null;
  exposure?: Record<string, unknown> | null;
}): number | null {
  const top = normalizeRiskLevel(provider.risk_level);
  if (top != null) return top;
  const exposure = provider.exposure;
  if (!exposure || typeof exposure !== 'object') return null;
  return normalizeExposureRiskScore(
    (exposure.riskScore as number | string | null | undefined) ??
      (exposure.risk_score as number | string | null | undefined),
  );
}
